import { useState, useEffect, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, ArrowRight, ArrowLeft } from "lucide-react";
import SEO, { breadcrumbSchema, faqSchema } from "../components/SEO";
import { Reveal } from "../components/Reveal";
import Breadcrumbs from "../components/Breadcrumbs";
import { Skeleton } from "../components/ui/skeleton";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { api } from "../lib/api";

const BASE = "https://numora.fun";
const INTERNAL_HOSTS = ["numora.fun", "www.numora.fun", "numoraa-premium.emergent.host", "numoraa-premium.preview.emergentagent.com"];

// Extract a /blog/<slug> slug from an href if it points to this site, else null.
function blogSlugFromHref(raw) {
  if (!raw) return null;
  let pathname = null;
  if (raw.startsWith("/")) pathname = raw;
  else if (/^https?:\/\//i.test(raw)) {
    try {
      const u = new URL(raw);
      if ([window.location.host, ...INTERNAL_HOSTS].includes(u.host)) pathname = u.pathname;
    } catch { return null; }
  }
  if (!pathname) return null;
  const m = pathname.match(/^\/blog\/([^/#?]+)/);
  return m ? m[1] : null;
}

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(() => {
  if (typeof window === "undefined") return null;

  const preloaded = window.__NUMORA_PRERENDERED_POST__;

  return preloaded && preloaded.slug === slug
    ? preloaded
    : null;
});
  const [error, setError] = useState(false);
  const [knownSlugs, setKnownSlugs] = useState(null); // Set of existing published slugs

  // Make internal links inside blog HTML work via the app router (and fix hard reloads)
  const onContentClick = (e) => {
    const a = e.target.closest("a");
    if (!a) return;
    const raw = (a.getAttribute("href") || "").trim();
    if (!raw || raw.startsWith("#") || raw.startsWith("mailto:") || raw.startsWith("tel:")) return;
    // Block links to blog posts that don't exist yet ("coming soon")
    const linkSlug = blogSlugFromHref(raw);
    if (linkSlug && knownSlugs && !knownSlugs.has(linkSlug)) { e.preventDefault(); return; }
    let path = null;
    if (raw.startsWith("/")) {
      path = raw;
    } else if (/^https?:\/\//i.test(raw)) {
      try {
        const u = new URL(raw);
        if ([window.location.host, ...INTERNAL_HOSTS].includes(u.host)) path = u.pathname + u.search + u.hash;
      } catch { /* ignore */ }
    }
    if (path) {
      if (path.startsWith("#")) return;
      e.preventDefault();
      navigate(path);
    }
  };

  useEffect(() => {
    api.get("/blog/posts", { params: { limit: 500 } })
      .then((r) => setKnownSlugs(new Set(r.data.map((p) => p.slug))))
      .catch(() => setKnownSlugs(new Set()));
  }, []);

  useEffect(() => {
    setPost(null); setError(false);
    api.get(`/blog/posts/${slug}`).then((r) => setPost(r.data)).catch(() => setError(true));
  }, [slug]);

  // Pre-process the HTML during render (not via DOM mutation, which React 19 wipes):
  // inject heading ids for the TOC and gracefully neutralize links to missing posts.
  const { html: processedHtml, toc } = useMemo(() => {
    if (!post || typeof window === "undefined") return { html: "", toc: [] };
    const doc = new DOMParser().parseFromString(post.content || "", "text/html");
    // EDIT A: the page template already shows the main H1, so remove any H1 inside the article body
    doc.querySelectorAll("h1").forEach((h) => h.remove());
    const items = [];
    doc.querySelectorAll("h2, h3").forEach((h, i) => {
      const id = `heading-${i}`;
      h.id = id;
      items.push({ id, text: h.textContent, level: h.tagName });
    });
    if (knownSlugs) {
      doc.querySelectorAll("a[href]").forEach((a) => {
        const linkSlug = blogSlugFromHref(a.getAttribute("href"));
        if (linkSlug && linkSlug !== post.slug && !knownSlugs.has(linkSlug)) {
          a.removeAttribute("href");
          a.classList.add("coming-soon");
          a.setAttribute("title", "This article is coming soon");
          const badge = doc.createElement("span");
          badge.className = "coming-soon-badge";
          badge.textContent = "coming soon";
          a.appendChild(badge);
        }
      });
    }
    return { html: doc.body.innerHTML, toc: items };
  }, [post, knownSlugs]);

  const articleSchema = useMemo(() => post && ({
    "@context": "https://schema.org", "@type": "Article",
    headline: post.title, image: post.cover_image, datePublished: post.published_at,
    dateModified: post.updated_at, author: { "@type": "Organization", name: post.author },
    publisher: { "@type": "Organization", name: "Numoraa" },
    description: post.meta_description,
  }), [post]);

  if (error) return (
  <>
    <SEO
      title="Article Not Found"
      noindex
      description="The requested Numoraa blog article could not be found. Explore more numerology articles."
      path={`/blog/${slug}`}
    />
    <div className="pt-40 pb-24 text-center px-5">
      <p className="font-serif text-6xl text-gold/30">✦</p>
      <h1 className="mt-4 font-serif text-4xl">Article not found</h1>
      <p className="mt-3 text-muted-foreground">
        This article doesn't exist or is no longer available.
      </p>
      <Link to="/blog" className="mt-6 inline-flex items-center gap-2 text-primary">
        <ArrowLeft className="h-4 w-4" /> Back to blog
      </Link>
    </div>
  </>
);

  if (!post) return (
    <div className="pt-32 max-w-3xl mx-auto px-5">
      <Skeleton className="h-8 w-2/3" /><Skeleton className="mt-4 h-72 w-full rounded-3xl" />
      <Skeleton className="mt-6 h-4 w-full" /><Skeleton className="mt-2 h-4 w-5/6" />
    </div>
  );

  const schemas = [
    articleSchema,
    breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Blog", path: "/blog" }, { name: post.title, path: `/blog/${post.slug}` }]),
  ];
  if (post.faqs?.length) schemas.push(faqSchema(post.faqs));

  return (
    <div className="pt-28 md:pt-32">
      <SEO title={post.meta_title || post.title} description={post.meta_description || post.excerpt} path={`/blog/${post.slug}`} image={post.cover_image} type="article" schema={schemas} />

      <article className="max-w-3xl mx-auto px-5 md:px-8">
        <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Blog", path: "/blog" }, { name: post.title, path: `/blog/${post.slug}` }]} />
        <Reveal>
          <p className="mt-6 text-xs uppercase tracking-[0.2em] text-gold">{post.category}</p>
          <h1 className="mt-3 font-serif text-4xl sm:text-5xl md:text-6xl font-light tracking-tighter leading-[1.05]">{post.title}</h1>
          <div className="mt-6 flex items-center gap-4 text-sm text-muted-foreground">
            <span>{post.author}</span>
            <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {post.reading_time} min read</span>
            {post.published_at && <span>{new Date(post.published_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</span>}
          </div>
        </Reveal>
      </article>

      <Reveal delay={0.1}>
        <div className="max-w-4xl mx-auto px-5 md:px-8 mt-10">
          <img src={post.cover_image} alt={post.title} className="w-full rounded-[2rem] object-cover max-h-[460px]" />
        </div>
      </Reveal>

      <div className="max-w-6xl mx-auto px-5 md:px-8 mt-14 grid lg:grid-cols-12 gap-10">
        {toc.length > 0 && (
          <aside className="hidden lg:block lg:col-span-3">
            <div className="sticky top-28">
              <p className="text-xs uppercase tracking-[0.2em] text-gold">On this page</p>
              <ul className="mt-4 space-y-2.5 text-sm" data-testid="blog-toc">
                {toc.map((t) => (
                  <li key={t.id} className={t.level === "H3" ? "pl-3" : ""}>
                    <a href={`#${t.id}`} className="text-muted-foreground hover:text-primary transition-colors">{t.text}</a>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        )}

        <div className={toc.length > 0 ? "lg:col-span-8" : "lg:col-span-10 lg:col-start-2"}>
          <div onClick={onContentClick} className="prose-numoraa" dangerouslySetInnerHTML={{ __html: processedHtml }} data-testid="blog-content" />

          {post.faqs?.length > 0 && (
            <div className="mt-14">
              <h2 className="font-serif text-3xl">Frequently Asked Questions</h2>
              <Accordion type="single" collapsible className="mt-6">
                {post.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="border-b border-border">
                    <AccordionTrigger className="text-left font-serif text-lg hover:no-underline py-5">{f.question}</AccordionTrigger>
                    <AccordionContent className="text-base text-muted-foreground pb-5">{f.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}

          {post.tags?.length > 0 && (
            <div className="mt-10 flex flex-wrap gap-2">
              {post.tags.map((t) => <span key={t} className="rounded-full bg-secondary px-3 py-1 text-xs text-muted-foreground">#{t}</span>)}
            </div>
          )}
        </div>
      </div>

      {post.related?.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 md:px-8 mt-24">
          <h2 className="font-serif text-3xl">Related articles</h2>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {post.related.map((r) => (
              <Link key={r.slug} to={`/blog/${r.slug}`} className="group overflow-hidden rounded-3xl border border-border bg-card shadow-soft transition-all hover:shadow-lift hover:-translate-y-1">
                {r.cover_image && <img src={r.cover_image} alt={r.title} loading="lazy" className="h-40 w-full object-cover transition-transform duration-700 group-hover:scale-105" />}
                <div className="p-5">
                  <h3 className="font-serif text-xl">{r.title}</h3>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-sm text-primary">Read <ArrowRight className="h-4 w-4" /></span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}